import { Controller } from "@hotwired/stimulus"
import { LegislationProcessor } from "../lib/auto_linker/legislation_processor"
import { escapeAttribute, escapeText } from "../lib/auto_linker/escape"
import { loadDictionary } from "../lib/auto_linker/dictionary"

/**
 * Processes assistant message content to auto-link legal references
 * using a client-side dictionary cached in localStorage.
 *
 * Detects patterns like:
 * - "art. 101 LCSP" / "artículo 82 de la Ley 40/2015" (article references)
 * - "DA 9 LCSP" / "Disposición Adicional Novena LCSP" (disposition references)
 * - "la Ley 9/2017" (standalone law references)
 *
 * The matching rules themselves live in LegislationProcessor, shared with the
 * Gobierto Conocimiento article bodies. What stays here is everything specific
 * to streamed assistant messages: the Turbo hook, the observer, the debounce and
 * which text is off-limits.
 *
 * Streaming strategy: instead of modifying the DOM after Turbo inserts content
 * (which causes a visible flash as links appear/disappear/reappear on each chunk),
 * we hook into `turbo:before-stream-render` to process the HTML *inside the
 * template* before Turbo inserts it. This way Turbo always inserts already-linked
 * HTML, making flash impossible.
 *
 * Links open the article page; preview pills trigger the
 * legislation--article-preview sliding panel.
 *
 * @value dictionaryUrl String - API endpoint for the dictionary JSON
 */
export default class extends Controller {
  static values = {
    dictionaryUrl: String,
    previewLabel: { type: String, default: "Preview" }
  }

  // ─── Lifecycle ────────────────────────────────────────────────────

  connect() {
    this._dictionary = null
    this._processor = null
    this._debounceTimers = new Map()
    this._nextKey = 0
    this._boundBeforeStreamRender = this._onBeforeStreamRender.bind(this)
    this._loadDictionary().then(() => {
      if (this._dictionary) {
        this._buildProcessor()
        this._processExistingMessages()
        this._startObserving()
        document.addEventListener("turbo:before-stream-render", this._boundBeforeStreamRender)
      }
    })
  }

  disconnect() {
    if (this._observer) {
      this._observer.disconnect()
      this._observer = null
    }
    this._debounceTimers.forEach(timer => clearTimeout(timer))
    this._debounceTimers.clear()
    document.removeEventListener("turbo:before-stream-render", this._boundBeforeStreamRender)
  }

  // ─── Dictionary loading ───────────────────────────────────────────

  // The endpoint answers 304 to the ETag it issued, which is a digest of the
  // dictionary's version rather than the version itself. Only the value the
  // server sent can be revalidated, so it is stored and replayed verbatim.
  async _loadDictionary() {
    this._dictionary = await loadDictionary(this.dictionaryUrlValue)
  }

  /**
   * The processor compiles the server's patterns and owns the matching rules.
   * Link markup is supplied here because it is assistant-specific: every article
   * gets a preview pill, which only works on a page that mounts the
   * legislation--article-preview panel.
   */
  _buildProcessor() {
    this._processor = new LegislationProcessor(this._dictionary, {
      // `label` arrives already escaped: it is a substring of the text the
      // processor escaped up front, so escaping it again would double it. The
      // slugs come from the dictionary rather than from prose, but they are
      // escaped anyway so that no value reaching an attribute is unescaped.
      buildArticleLink: ({ lawSlug, articleSlug, label }) => {
        const href = `/normativa/${escapeAttribute(lawSlug)}/${escapeAttribute(articleSlug)}`
        return `<a href="${href}" class="text-blue no-underline hover:underline" target="_blank">${label}</a>` +
          `<a href="${href}" class="article-preview-pill" ` +
          `data-action="click->legislation--article-preview#open" ` +
          `data-preview-url="${href}/preview">` +
          `<span role="tooltip" aria-label="${escapeAttribute(this.previewLabelValue)}" data-microtip-position="top">` +
          `${escapeText(this.previewLabelValue)}</span></a>`
      },
      buildLawLink: ({ lawSlug, label }) =>
        `<a href="/normativa/${escapeAttribute(lawSlug)}" class="text-blue no-underline hover:underline" target="_blank">${label}</a>`
    })
  }

  // ─── Turbo Stream interception ──────────────────────────────────

  /**
   * Intercept Turbo Stream updates that target message answers. By processing
   * the template content BEFORE Turbo inserts it into the DOM, the browser
   * never renders an un-linked intermediate state — no flash.
   */
  _onBeforeStreamRender(event) {
    const streamEl = event.target
    const target = streamEl.getAttribute("target")

    // Only process updates to message answer containers
    if (!target?.startsWith("message-answer-")) return

    const template = streamEl.querySelector("template")
    if (!template) return

    const messageContent = template.content.querySelector(".assistant--message-content")
    if (messageContent) {
      this._processElement(messageContent)
    }
  }

  // ─── MutationObserver ─────────────────────────────────────────────
  // Handles non-streaming scenarios: page load, Turbo navigation, etc.

  _startObserving() {
    this._observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const msgEl = node.classList?.contains("assistant--message-content")
              ? node
              : node.querySelector?.(".assistant--message-content")
            if (msgEl) {
              this._scheduleProcessing(msgEl)
            }
          }
        }
      }
    })

    this._observer.observe(this.element, { childList: true, subtree: true })
  }

  _scheduleProcessing(messageEl) {
    if (!messageEl._autoLinkerKey) {
      messageEl._autoLinkerKey = `msg-${this._nextKey++}`
    }
    const key = messageEl._autoLinkerKey

    if (this._debounceTimers.has(key)) {
      clearTimeout(this._debounceTimers.get(key))
    }
    this._debounceTimers.set(key, setTimeout(() => {
      this._debounceTimers.delete(key)
      this._processElement(messageEl)
    }, 500))
  }

  // ─── Processing ───────────────────────────────────────────────────

  _processExistingMessages() {
    this.element.querySelectorAll(".assistant--message-content").forEach(el => {
      this._processElement(el)
    })
  }

  _processElement(el) {
    if (!this._processor || this._processor.empty) return

    // Detect the most-mentioned law in this element so bare refs like "Art. 29"
    // (without a law suffix) can be resolved against it.
    const contextLawSlug = this._processor.detectContextLaw(el)

    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (node.parentElement?.closest("a, .assistant-message-tool-call, .tool-call-item")) return NodeFilter.FILTER_REJECT
        if (node.textContent.trim().length < 5) return NodeFilter.FILTER_SKIP
        return NodeFilter.FILTER_ACCEPT
      }
    })

    const textNodes = []
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode)
    }

    for (const node of textNodes) {
      this._processor.processTextNode(node, contextLawSlug)
    }
  }
}
